import React, { useCallback, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

/**
 * Generic, manually-controlled carousel (no autoplay).
 * `slides` is an array of { key, content }. This component only
 * owns slide-switching state/UI, callers decide what each slide renders.
 */
const Carousel = ({ slides, ariaLabel }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const slideCount = slides.length;

  const goTo = useCallback(
    (index) => setActiveIndex(((index % slideCount) + slideCount) % slideCount),
    [slideCount]
  );

  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', borderRadius: 4 }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          transform: `translateX(-${activeIndex * 100}%)`,
          transition: 'transform 0.5s ease',
        }}
      >
        {slides.map((slide, i) => (
          <Box key={slide.key ?? i} sx={{ flex: '0 0 100%', height: '100%' }}>
            {slide.content}
          </Box>
        ))}
      </Box>

      {slideCount > 1 && (
        <>
          <IconButton
            onClick={() => goTo(activeIndex - 1)}
            aria-label="Previous slide"
            size="small"
            sx={{
              position: 'absolute',
              top: '50%',
              left: { xs: 8, md: 16 },
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            onClick={() => goTo(activeIndex + 1)}
            aria-label="Next slide"
            size="small"
            sx={{
              position: 'absolute',
              top: '50%',
              right: { xs: 8, md: 16 },
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': { bgcolor: 'background.paper' },
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              bottom: 14,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
            }}
          >
            {slides.map((slide, i) => (
              <Box
                key={slide.key ?? i}
                onClick={() => goTo(i)}
                role="button"
                aria-label={`Go to slide ${i + 1}`}
                sx={{
                  width: i === activeIndex ? 22 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: i === activeIndex ? 'primary.main' : 'rgba(255,255,255,0.7)',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                  cursor: 'pointer',
                  transition: 'width 0.3s ease, background-color 0.3s ease',
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Carousel;
